import axios from "axios";

// const endPointUrl = "192.168.188.100:3011/api";
// const endPointUrl = "http://localhost:3111/api";
const endPointUrl = "https://break.dulcecomercio.com/api";

// Base configuration for all API requests
export const apiClient = axios.create({
  baseURL: endPointUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Universal Axios Request Helper
export const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    const response = await apiClient.request({
      method,
      url,
      data,
      ...config,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.log("API Error:", error.message, error.response?.data);

    // Handle network errors
    if (error.message === "Network Error") {
      return {
        success: false,
        message: "No internet connection. Please check your network and try again.",
      };
    }

    // Handle timeout or server unreachable
    if (error.code === "ECONNABORTED") {
      return {
        success: false,
        message: "The request timed out. Please try again later.",
      };
    }

    // Handle API errors
    if (error.response) {
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        (status === 401
          ? "Unauthorized. Please log in again."
          : status === 500
          ? "Server error. Please try again later."
          : "Something went wrong.");

      return {
        success: false,
        message,
        status,
      };
    }

    // Fallback for unknown errors
    return {
      success: false,
      message: "An unexpected error occurred. Please try again.",
    };
  }
};


apiClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request OTP endpoint
export const bindEmail = async (email) => {
  try {
    const payload = { email };
    const response = await apiClient.post("dulce/auth/bind-email", payload);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Set password and signup
export const signup = async (email, password, referralCode) => {
  try {
    const payload = {email, password };
    if (referralCode) {
      payload.referralCode = referralCode;
    }
    const response = await apiClient.post("dulce/auth/signup", payload);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Verify OTP endpoint
export const verifyEmail = async (email, otp) => {
  try {
    const response = await apiClient.post("/dulce/auth/verify-email", {
      email,
      otp,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Verify OTP endpoint
export const resendOTP = async (email) => {
  try {
    const response = await apiClient.post("/dulce/auth/resend-otp", {
      email
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const payload = { email, password };
    const response = await apiClient.post("/dulce/auth/login", payload);
    return response.data;
  } catch (error) {
    console.log("Response data:", error);
    throw error;
  }
};

// Login endpoint
export const forgotPassword = async (email) => {
  try {
    const payload = { email };
    const response = await apiClient.post("/dulce/auth/request-password-reset", payload);
    return response.data;
  } catch (error) {
    console.log("Response data:", error);
    throw error;
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const payload = { token, newPassword };
    const response = await apiClient.post("/dulce/auth/reset-password", payload);
    return response.data;
  } catch (error) {
    console.log("Response data:", error);
    throw error;
  }
};

export const fetchUserInfo = async () => {
  try {
    const res = await apiClient.get('/dulce/auth/me');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch user info:", error.response?.data || error.message);
    throw error;
  }
};

export const updateUserInfo = async () => {
  try {
    const res = await apiClient.put('/dulce/auth/update-profile');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch user info:", error.response?.data || error.message);
    throw error;
  }
};

export const fetchWalletInfo = async () => {
  try {
    const res = await apiClient.get('/dulce/get-wallet');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet data:", error.response?.data || error.message);
    throw error;
  }
};

export const deleteUser = async () => {
  try {
    const res = await apiClient.delete(`/dulce/users/${userId}`);
  return res.data;
  } catch (error) {
    console.log("Failed to delete:", error.response?.data || error.message);
    throw error;
  }
};

export const fetchUserSubscriptions = async () => {
  try {
    const res = await apiClient.get('/dulce/subscriptions/user');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet data:", error.response?.data || error.message);
    throw error;
  }
};

export const getRewardValue = async () => {
  try {
    const res = await apiClient.get('/dulce/subscriptions/claimable');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet data:", error.response?.data || error.message);
    throw error;
  }
};

export const claimReward = async () => {
  try {
    const res = await apiClient.post('/dulce/plan/claim');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet data:", error.response?.data || error.message);
    throw error;
  }
};

export const subscribe = async (planId, currency) => {
  try {
    const payload = {planId, currency}
    const res = await apiClient.post('/dulce/plan/subscribe', payload);
  return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet data:", error.response?.data || error.message);
    throw error;
  }
};

export const transactionHistory = async ({ page = 1, limit = 10, type, status } = {}) => {
  try {
    const params = new URLSearchParams({ page, limit });
    if (type) params.append('type', type);
    if (status) params.append('status', status);

    const res = await apiClient.get(`/dulce/transactions/user-transactions?${params.toString()}`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch transactions:", error.response?.data || error.message);
    throw error;
  }
};

export const withdrawFunds = async (receiverAddress, amount, currency) => {
  try {
    const payload = { receiverAddress, amount, currency };
    const response = await apiClient.post("/dulce/withdraw", payload);
    return response.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

export const withdrawRef = async (receiverAddress, amount) => {
  try {
    const payload = { receiverAddress, amount };
    const response = await apiClient.post("dulce/referrals/withdraw", payload);
    return response.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

export const fetchReferralTiers = async () => {
  try {
    const res = await apiClient.get('/dulce/referrals/tiers');
    return res.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

export const fetchReferrals = async () => {
  try {
    const res = await apiClient.get('/dulce/referrals/get-referred-users');
    return res.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

export const fetchReferralsStatus = async () => {
  try {
    const res = await apiClient.get('/dulce/referrals/check-status');
    return res.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};

export const fetchFoods = async () => {
  try {
    const res = await apiClient.get('/dulce/foods');
    return res.data;
  } catch (error) {
    console.log("data not fetched", error.response?.data || error.message);
    throw error;
  }
};



